import { AlertCircle, Bell } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyStateBlock } from '@/components/patterns/EmptyStateBlock';
import { ContentContainer } from '@/components/shell/ContentContainer';
import {
  listNotifications,
  markAllRead,
  markRead,
  type NotificationDto,
} from '@/features/notifications/api';
import { clearUnreadCountCache } from '@/hooks/useUnreadNotificationCount';
import { ApiError, NetworkError } from '@/lib/api-client';
import { WEB_PUSH_DELIVERY_ENABLED } from '@/lib/config';
import { supportsPushNotifications } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

const PWA_PUSH_AVAILABLE = supportsPushNotifications() && WEB_PUSH_DELIVERY_ENABLED;

type LoadError = 'load' | 'network' | null;

const RELATIVE_TIME_FORMATTER =
  typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
    ? new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' })
    : null;
const ABSOLUTE_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatTimestamp(iso: string): string {
  const created = new Date(iso);
  const ageMs = Date.now() - created.getTime();
  const ageMin = ageMs / 60_000;
  if (ageMin < 60 && RELATIVE_TIME_FORMATTER) {
    const minutes = Math.max(1, Math.round(ageMin));
    return RELATIVE_TIME_FORMATTER.format(-minutes, 'minute');
  }
  if (ageMin < 1440 && RELATIVE_TIME_FORMATTER) {
    return RELATIVE_TIME_FORMATTER.format(-Math.round(ageMin / 60), 'hour');
  }
  return ABSOLUTE_DATE_FORMATTER.format(created);
}

export function NotificationsRoute() {
  const navigate = useNavigate();
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const pushConsent = useAuthStore((s) => s.user?.pushConsent ?? 'never_asked');

  const [items, setItems] = useState<NotificationDto[] | null>(null);
  const [loadError, setLoadError] = useState<LoadError>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pushSaving, setPushSaving] = useState(false);

  const S = STRINGS.account.notifications;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listNotifications();
        if (cancelled) return;
        setItems(res.items);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) setLoadError('network');
        else setLoadError('load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const unreadCount = items?.filter((n) => n.readAt === null).length ?? 0;

  const handleCardClick = async (n: NotificationDto) => {
    setActionError(null);
    if (n.readAt === null) {
      try {
        await markRead(n.id);
        setItems(
          (prev) =>
            prev?.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)) ??
            null,
        );
        clearUnreadCountCache();
      } catch (err) {
        if (err instanceof NetworkError) {
          setActionError(S.errors.networkError);
        } else if (err instanceof ApiError) {
          setActionError(S.errors.markReadFailed);
        } else {
          setActionError(S.errors.markReadFailed);
        }
        // Don't navigate on markRead failure — user can retry.
        return;
      }
    }
    if (n.ctaUrl) navigate(n.ctaUrl);
  };

  const handleMarkAllRead = async () => {
    setActionError(null);
    try {
      await markAllRead();
      const now = new Date().toISOString();
      setItems((prev) => prev?.map((x) => (x.readAt === null ? { ...x, readAt: now } : x)) ?? null);
      clearUnreadCountCache();
    } catch (err) {
      if (err instanceof NetworkError) {
        setActionError(S.errors.networkError);
      } else {
        setActionError(S.errors.markReadFailed);
      }
    }
  };

  const handlePushToggle = async (next: boolean) => {
    setPushSaving(true);
    try {
      await updateProfile({ pushConsent: next ? 'asked_yes' : 'asked_no' });
    } catch {
      // Silent — toggle reverts on next render via authStore source-of-truth.
    } finally {
      setPushSaving(false);
    }
  };

  const loadErrorMessage =
    loadError === 'network'
      ? S.errors.networkError
      : loadError === 'load'
        ? S.errors.loadFailed
        : null;

  // Array.isArray guard (not `items !== null`) makes render survive any
  // future API contract drift — if items is ever undefined or non-array
  // (e.g. envelope/bare-array contract mismatch), we render empty-state
  // instead of crashing on items.length. Was the root cause of the
  // bell-tap crash before the api.ts envelope-wrap fix.
  const isEmpty = Array.isArray(items) && items.length === 0;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="default">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {S.back}
          </button>

          <div className="flex items-center justify-between mb-4">
            <h1 className="text-[22px] font-semibold text-dream-navy">{S.title}</h1>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                className="text-sm text-dream-navy/70 hover:text-dream-navy underline transition-colors"
                data-testid="notifications-mark-all-read"
              >
                {S.markAllRead}
              </button>
            )}
          </div>

          {loadErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{loadErrorMessage}</span>
            </div>
          )}

          {actionError && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{actionError}</span>
            </div>
          )}

          {Array.isArray(items) && items.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid="notifications-list">
              {items.map((n) => {
                const isUnread = n.readAt === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void handleCardClick(n)}
                      data-testid={`notification-card-${n.id}`}
                      data-unread={isUnread ? 'true' : 'false'}
                      className={cn(
                        'w-full text-left rounded-lg border p-3 transition-colors',
                        // Order matters — tailwind-merge dedupes by category;
                        // place the directional `border-l-*` colour AFTER the
                        // base `border-*` colour so it wins.
                        isUnread
                          ? 'border-dream-navy/10 border-l-4 border-l-world-aa bg-world-aa/5'
                          : 'border-dream-navy/10 bg-dream-white opacity-60',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            'text-sm font-semibold text-dream-navy',
                            !isUnread && 'font-medium',
                          )}
                        >
                          {n.title}
                        </p>
                        {isUnread && (
                          <span
                            className="shrink-0 rounded-full bg-world-aa px-1.5 py-0.5 text-xxs font-semibold uppercase text-dream-navy"
                            data-testid={`notification-new-badge-${n.id}`}
                          >
                            {S.newBadge}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-dream-navy/70">{n.body}</p>
                      <p className="mt-1 text-xxs text-dream-navy/50">
                        {formatTimestamp(n.createdAt)}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {isEmpty && !loadErrorMessage && (
            <EmptyStateBlock
              icon={<Bell className="h-10 w-10 text-dream-navy/40" aria-hidden="true" />}
              heading={S.empty}
              body=""
              testId="notifications-empty-state"
            />
          )}

          {PWA_PUSH_AVAILABLE && (
            <section
              className="mt-8 rounded-lg border border-dream-navy/10 bg-dream-white p-4"
              data-testid="notifications-push-toggle-section"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-dream-navy">{S.pushToggle.label}</p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={pushConsent === 'asked_yes'}
                    disabled={pushSaving}
                    onChange={(e) => void handlePushToggle(e.target.checked)}
                    data-testid="notifications-push-toggle-input"
                  />
                  <div className="w-10 h-6 bg-dream-navy/15 rounded-full peer-checked:bg-[color:var(--color-success)] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-dream-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
              <p className="mt-1 text-xs text-dream-navy/60">{S.pushToggle.info}</p>
            </section>
          )}
        </div>
      </ContentContainer>
    </div>
  );
}
